/**
 * RUM SLI/SLO Tracking Utilities
 *
 * Provides journey timing state management for Apdex calculation
 * and AI operation timing for P95/P99 SLOs.
 *
 * @see docs/rum-actions-map.md for full documentation
 */
import { datadogRum } from '@datadog/browser-rum';

/**
 * Journey timing state management for Apdex calculation
 */
interface JourneyTiming {
  startTime: number;
  journeyName: string;
  satisfiedThreshold: number;   // T in Apdex
  toleratingThreshold: number;  // 4T in Apdex
}

const activeJourneys: Map<string, JourneyTiming> = new Map();

/**
 * Apdex thresholds by journey (in milliseconds)
 */
export const APDEX_THRESHOLDS = {
  specification_creation: { satisfied: 5000, tolerating: 20000 },
  product_discovery: { satisfied: 10000, tolerating: 40000 },
  product_comparison: { satisfied: 15000, tolerating: 60000 },
  collaboration: { satisfied: 3000, tolerating: 10000 },
  authentication: { satisfied: 1000, tolerating: 3000 },
  report_generation: { satisfied: 30000, tolerating: 120000 },
} as const;

export type JourneyName = keyof typeof APDEX_THRESHOLDS;

/**
 * Start tracking a journey for Apdex calculation
 */
export const startJourneyTiming = (
  journeyId: string,
  journeyName: JourneyName
): void => {
  const thresholds = APDEX_THRESHOLDS[journeyName];
  activeJourneys.set(journeyId, {
    startTime: Date.now(),
    journeyName,
    satisfiedThreshold: thresholds.satisfied,
    toleratingThreshold: thresholds.tolerating,
  });
};

/**
 * Complete journey and calculate Apdex category
 */
export const completeJourneyTiming = (
  journeyId: string,
  success: boolean = true
): {
  duration_ms: number;
  apdex_category: 'satisfied' | 'tolerating' | 'frustrated';
  apdex_threshold_satisfied: number;
  apdex_threshold_tolerating: number;
} | null => {
  const timing = activeJourneys.get(journeyId);
  if (!timing) return null;

  const duration = Date.now() - timing.startTime;
  let apdexCategory: 'satisfied' | 'tolerating' | 'frustrated';

  if (!success) {
    apdexCategory = 'frustrated';
  } else if (duration <= timing.satisfiedThreshold) {
    apdexCategory = 'satisfied';
  } else if (duration <= timing.toleratingThreshold) {
    apdexCategory = 'tolerating';
  } else {
    apdexCategory = 'frustrated';
  }

  activeJourneys.delete(journeyId);

  return {
    duration_ms: duration,
    apdex_category: apdexCategory,
    apdex_threshold_satisfied: timing.satisfiedThreshold,
    apdex_threshold_tolerating: timing.toleratingThreshold,
  };
};

/**
 * Cancel a journey timing without completing it
 */
export const cancelJourneyTiming = (journeyId: string): void => {
  activeJourneys.delete(journeyId);
};

/**
 * Check if a journey is currently being timed
 */
export const isJourneyActive = (journeyId: string): boolean => {
  return activeJourneys.has(journeyId);
};

/**
 * P95/P99 thresholds for AI operations
 */
export const AI_SLO_THRESHOLDS = {
  generation: { p95: 30000, p99: 60000 },
  verification: { p95: 20000, p99: 45000 },
  ranking: { p95: 60000, p99: 120000 },
  chat: { p95: 10000, p99: 20000 },
} as const;

export type AIOperationType = keyof typeof AI_SLO_THRESHOLDS;

/**
 * Track AI operation timing for P95/P99 SLOs
 */
export const trackAIOperationTiming = (
  operationId: string,
  operationType: AIOperationType,
  durationMs: number,
  success: boolean
): void => {
  const thresholds = AI_SLO_THRESHOLDS[operationType];

  datadogRum.addAction(`ai.${operationType}.timing`, {
    ai: {
      operation_id: operationId,
      operation_type: operationType,
      duration_ms: durationMs,
      success: success,
      within_p95: durationMs <= thresholds.p95,
      within_p99: durationMs <= thresholds.p99,
      slo_threshold_p95: thresholds.p95,
      slo_threshold_p99: thresholds.p99,
    }
  });
};

/**
 * Track page route for per-page Core Web Vitals
 */
export const setPageContext = (route: string, pageType: string): void => {
  datadogRum.setGlobalContextProperty('page', {
    route: route,
    type: pageType,
    timestamp: Date.now(),
  });
};

/**
 * Save/Specification SLO thresholds
 */
export const SAVE_SLO_THRESHOLDS = {
  specification: { p95: 2000, p99: 5000 },
  report: { p95: 30000, p99: 60000 },
} as const;

/**
 * Check if operation duration is within SLO thresholds
 */
export const isWithinSLO = (
  operationType: keyof typeof SAVE_SLO_THRESHOLDS,
  durationMs: number,
  percentile: 'p95' | 'p99'
): boolean => {
  const thresholds = SAVE_SLO_THRESHOLDS[operationType];
  return durationMs <= thresholds[percentile];
};
